import React, { useState } from 'react';
import appSettingsService, { PermissionSyncResult } from '../../services/appSettings.service';

/**
 * Permission sync — prod → this environment (Application Settings).
 *
 * Merges every permission table from prod (endpoints, pages, group grants,
 * component permissions, PII, roles, memberships): prod-wins upserts, and
 * deletes only grants prod genuinely revoked — grants for test-only features
 * survive. Preview is read-only; Apply re-plans server-side and snapshots
 * every table it touches before writing, so it is safe to re-run.
 *
 * The card renders only on non-prod builds (see ApplicationSettings.tsx) and
 * the backend independently refuses unless PERM_SYNC_ENABLED is set and the
 * target schema differs from the source.
 */
const PermissionSyncPanel: React.FC = () => {
  const [result, setResult] = useState<PermissionSyncResult | null>(null);
  const [applied, setApplied] = useState<boolean>(false);
  const [busy, setBusy] = useState<'preview' | 'apply' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const runPreview = async () => {
    setBusy('preview');
    setError(null);
    setApplied(false);
    setResult(null);
    try {
      setResult(await appSettingsService.previewPermissionSync());
    } catch {
      setError('Failed to preview the permission sync.');
    } finally {
      setBusy(null);
    }
  };

  const runApply = async () => {
    setBusy('apply');
    setError(null);
    try {
      setResult(await appSettingsService.applyPermissionSync());
      setApplied(true);
    } catch {
      setError('Failed to apply the permission sync.');
    } finally {
      setBusy(null);
    }
  };

  const changedTables = (result?.tables ?? []).filter(
    (t) => t.inserts + t.updates + t.deletes > 0
  );
  const totalChanges = result?.total_changes ?? 0;
  const showApply = !!result && !applied && totalChanges > 0;

  return (
    <div className="bg-panel shadow rounded-lg p-6 mt-6">
      <h2 className="text-lg font-medium text-heading mb-1">
        Sync Permissions from Prod
      </h2>
      <p className="text-xs text-muted mb-4">
        Copies endpoint registrations, page assignments, group grants, component
        permissions, PII grants, roles and memberships from production into this
        environment. Prod wins on differences and revocations propagate, but grants for
        features that only exist here are left alone. Apply snapshots every table it
        touches first and re-plans server-side. Backend permission caches refresh within
        ~5 minutes of an apply.
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="flex items-center gap-3 mb-4">
        <button
          onClick={runPreview}
          disabled={busy !== null}
          className="px-3 py-1.5 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {busy === 'preview' ? 'Previewing…' : 'Preview sync'}
        </button>
        {showApply && (
          <button
            onClick={runApply}
            disabled={busy !== null}
            className="px-3 py-1.5 text-sm font-medium rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
          >
            {busy === 'apply' ? 'Applying…' : `Apply sync (${totalChanges} changes)`}
          </button>
        )}
      </div>

      {result && applied && (
        <p className="mb-4 text-sm font-medium text-green-700 dark:text-green-400">
          Applied {totalChanges} changes. Snapshots of every touched table were saved first.
        </p>
      )}

      {result && totalChanges === 0 && (
        <p className="text-sm text-label">
          Already in sync — production and this environment match.
        </p>
      )}

      {changedTables.length > 0 && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-md divide-y divide-gray-200 dark:divide-gray-700">
          {changedTables.map((t) => (
            <div key={t.table} className="flex items-center justify-between px-4 py-2">
              <span className="text-sm font-medium text-heading">
                {t.table}
              </span>
              <span className="text-sm text-label">
                <span className="text-green-700 dark:text-green-400">+{t.inserts}</span>
                <span className="mx-2 text-amber-700 dark:text-amber-400">~{t.updates}</span>
                <span className="text-red-700 dark:text-red-400">−{t.deletes}</span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PermissionSyncPanel;
